import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../../theme/ThemeContext';
import ThemeToggle from '../ThemeToggle/ThemeToggle';
import styles from './Navigation.module.css';
import ScoreDisplay from '../ScoreDisplay/ScoreDisplay';

// Import icons from react-icons
import { FaGithub, FaLinkedin } from 'react-icons/fa6';

interface NavigationProps {
  score?: number;
}

const Navigation: React.FC<NavigationProps> = ({ score = 0 }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const isGameField = location.pathname === '/game';

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Close the menu when a navigation item is clicked
  const handleNavItemClick = () => {
    setIsMenuOpen(false);
  };

  // Detect if the screen is small
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 480);

  useEffect(() => {
    // Handle window resize
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 480);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      {/* Mobile score display (fixed position) */}
      {isGameField && isSmallScreen && (
        <ScoreDisplay score={score} isMobile={true} />
      )}
      
      <nav className={styles.navbar}>
        <div className={styles.container}>
          {/* Left Side: Menu Icon and Logo */}
          <div className={styles.leftContainer}>
            {/* Menu Icon */}
            <button
              type="button"
              className={`${styles.menuIcon} ${isMenuOpen ? styles.active : ''}`}
              onClick={toggleMenu}
              aria-label="Toggle navigation menu"
              aria-expanded={isMenuOpen}
            >
              <span className={styles.menuIconLine}></span>
              <span className={styles.menuIconLine}></span>
              <span className={styles.menuIconLine}></span>
            </button>

            {/* Logo */}
            <Link to="/" className={styles.logo} onClick={handleNavItemClick}>
              <img src={isDarkMode ? "/nameLogoDark.png" : "/nameLogoLight.png"} alt="Conor Hutchins"
                width="200"
                height="80"
              className={styles.logoImg}/>
            </Link>
            
            {/* Score display in navbar for game field (only on larger screens) */}
            {isGameField && !isSmallScreen && (
              <div className={styles.scoreContainer}>
                <ScoreDisplay score={score} />
              </div>
            )}
          </div>

        {/* Social Icons (Centered) */}
        <div className={styles.socialIcons}>
          <a
            href="https://github.com/conorhutchins"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.socialIconLink}
          >
            <FaGithub className={styles.socialIcon} />
          </a>
          <a
            href="https://www.linkedin.com/in/conorhutchins/"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.socialIconLink}
          >
            <FaLinkedin className={styles.socialIcon} />
          </a>
        </div>

        {/* Theme Toggle (Right Side) */}
        
          <ThemeToggle />
        

        {/* Navigation Links (Hidden by Default, Shown on Menu Open) */}
        <ul className={`${styles.navList} ${isMenuOpen ? styles.active : ''}`}>
          <li className={styles.navItem}>
            <Link to="/experience" className={styles.navLink} onClick={handleNavItemClick}>
              Experience
            </Link>
          </li>
          <li className={styles.navItem}>
            <Link to="/projects" className={styles.navLink} onClick={handleNavItemClick}>
              Projects
            </Link>
          </li>
          <li className={styles.navItem}>
            <Link to="/contact" className={styles.navLink} onClick={handleNavItemClick}>
              Contact
            </Link>
          </li>
          <li className={styles.navItem}>
            <Link to="/techstack" className={styles.navLink} onClick={handleNavItemClick}>
              Tech Stack
            </Link>
          </li>
          <li className={styles.navItem}>
            <a
              href="https://docs.google.com/document/d/1Wt0oxhnKdLZbKuRXHW5mvVp4afUQ3zYGDye5OFnWO1Y/edit?tab=t.0"
              className={styles.navLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleNavItemClick}
            >
              C.V
            </a>
          </li>
        </ul>
      </div>
    </nav>
    </>
  );
};

export default Navigation;
